import React, { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

import { HERO_CAROUSEL_HEIGHT } from '../../constants/fullScreenCarousel';

function getOrientation() {
  let orientation = 'landscape';
  if (process.browser && window.orientation % 180 === 0) {
    orientation = 'portrait';
  }
  return orientation;
}

const VideoWrapper = styled.div`
  position: absolute;
  height: calc(100% - 97px);
  width: 100%;
  z-index: ${props => props.theme.zIndex.increment};
`;

const NoPointerEvents = styled.div`
  pointer-events: none;
  height: 100%;
  width: 100%;
`;

const NoPointerEventsHeroCarousel = styled(NoPointerEvents)`
  height: ${HERO_CAROUSEL_HEIGHT};
`;

const VideoSizingWrapper = styled.div`
  height: 100%;

  ${props =>
    props.backgroundImage &&
    `
      background: url(${props.backgroundImage});

      & > div > div {
        background-color: transparent !important;
      }
  `}

  & > div > div > div {
    ${props =>
      props.videoFitDimensions &&
      `
        // We use padding-top to fit the video to the box. The
        // equation below will essentially create a value that
        // resizes with the browser like: padding: 57.25% 0 0 0
        padding: ${
          100 / (props.videoFitDimensions[0] / props.videoFitDimensions[1])
        }% 0 0 0 !important;
        height: 100%;
    `}
  }
`;

function VideoCarouselItem({
  asset,
  onEnded = () => null,
  onPause = () => null,
  onPlay = () => null,
  onVideoClick,
  playing,
  shouldLoopInBackground,
  swipeDate,
  videoFitDimensions = [],
  videoHeight,
  videoOptions,
  ...rest
}) {
  const [orientation, setOrientation] = useState(getOrientation());
  const [isPlaying, setIsPlaying] = useState(
    videoOptions.controls ? videoOptions.playing : playing
  );
  const [mouseMoveDate, setMouseMoveDate] = useState(new Date());

  useEffect(() => {
    function handleOrientationChange() {
      setOrientation(getOrientation());
    }

    window.addEventListener('orientationchange', handleOrientationChange);
    return () =>
      window.removeEventListener('orientationchange', handleOrientationChange);
  }, [setOrientation]);

  const { options } = asset;
  const { customVars } = options;
  const { config, controls, muted, loop, style } = videoOptions;
  const shouldShowVideoControls = controls;

  // If it's a `videoUrl` with images attached, we assume that
  // those images are a background to put behind the video
  const isPortrait = orientation === 'portrait';
  let backgroundImage;
  if (asset.mobileImageFilename && asset.imageFilename) {
    backgroundImage = isPortrait
      ? asset.mobileImageFilename
      : asset.imageFilename;
  }

  const NoPointerEventsWrapper = shouldLoopInBackground
    ? NoPointerEventsHeroCarousel
    : NoPointerEvents;
  const wrapper = shouldShowVideoControls ? undefined : NoPointerEventsWrapper;

  const handleVideoClick = () => {
    // If it's a looping background video, ignore all clicks
    if (shouldLoopInBackground) {
      return;
    }

    // To prevent `onClick` from accidentally starting video during carousel
    // swipe, we use this 500ms buffer from the last onMouseMove state change
    if (new Date() - mouseMoveDate < 500 || isPlaying) {
      return handlePause();
    }

    handlePlay();
    onVideoClick && onVideoClick();
  };

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay();
  };

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPause();
  }, [onPause]);

  const handleMouseMove = e => {
    // Ignore mobile devices that emulate onMouseMove
    if ('ontouchstart' in window) {
      return;
    }

    if (!e.buttons) {
      return;
    }

    // Only run every 100ms to prevent excessive re-renders
    const date = new Date();
    if (date - mouseMoveDate > 100) {
      setMouseMoveDate(date);
    }
  };

  // Pause if there is a swipe event
  useEffect(() => {
    // Ignore if video should loop forever in background
    if (shouldLoopInBackground) {
      return;
    }

    handlePause();
  }, [handlePause, shouldLoopInBackground, swipeDate]);

  return (
    <VideoSizingWrapper
      backgroundImage={backgroundImage}
      videoFitDimensions={videoFitDimensions}
    >
      <VideoWrapper onClick={handleVideoClick} onMouseMove={handleMouseMove} />
      <ReactPlayer
        height={
          shouldLoopInBackground ? HERO_CAROUSEL_HEIGHT : videoHeight || '100%'
        }
        width="100%"
        config={config}
        controls={controls}
        loop={loop}
        muted={muted}
        onEnded={onEnded}
        onPause={handlePause}
        onPlay={handlePlay}
        playing={isPlaying}
        style={style}
        url={
          isPortrait && customVars.videoUrlPortrait
            ? customVars.videoUrlPortrait
            : customVars.videoUrl
        }
        wrapper={wrapper}
        {...rest}
      />
    </VideoSizingWrapper>
  );
}

VideoCarouselItem.propTypes = {
  asset: PropTypes.object,
  onEnded: PropTypes.func,
  onPause: PropTypes.func,
  onPlay: PropTypes.func,
  onVideoClick: PropTypes.func,
  options: PropTypes.object,
  playing: PropTypes.bool,
  shouldLoopInBackground: PropTypes.bool,
  swipeDate: PropTypes.object,
  videoFitDimensions: PropTypes.array,
  videoHeight: PropTypes.number,
  videoOptions: PropTypes.object,
};

export default VideoCarouselItem;
